import { useEffect, useState, type CSSProperties } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { usePowerSyncDb } from '../../adapters/powersync';
import {
  scanInventoryItemReferences,
  deleteInventoryItemCascade,
  type InventoryDeleteScan,
} from '../../services/delete-inventory-item-service';

/**
 * Admin delete for an inventory item. Scans related data on open: package
 * definitions cascade (shown under "will remove"), but deletion is blocked when
 * the item is referenced by financial / clinical / stock history.
 */
export function DeleteInventoryItemModal({
  open,
  onOpenChange,
  itemId,
  itemName,
  portalStyle,
  onDeleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: string;
  itemName: string;
  portalStyle?: CSSProperties;
  onDeleted?: () => void;
}) {
  const { db } = usePowerSyncDb();
  const [scan, setScan] = useState<InventoryDeleteScan | null>(null);
  const [scanning, setScanning] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Scan whenever the modal opens for a given item.
  useEffect(() => {
    if (!open || !db) return;
    let cancelled = false;
    setScan(null);
    setError(null);
    setScanning(true);
    scanInventoryItemReferences(db, itemId)
      .then((result) => {
        if (!cancelled) setScan(result);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to scan related data.');
      })
      .finally(() => {
        if (!cancelled) setScanning(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, db, itemId]);

  const blocked = !scan || scan.blockers.length > 0;

  async function handleConfirm() {
    if (!db || !scan?.canDelete) return;
    setError(null);
    setWorking(true);
    try {
      const res = await deleteInventoryItemCascade(db, itemId);
      if (!res.deleted) {
        setError(res.error ?? 'Delete failed. Please try again.');
        setWorking(false);
        return;
      }
      onDeleted?.();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed. Please try again.');
      setWorking(false);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onOpenChange(false)}
      title="Delete Inventory Item"
      maxWidth={520}
      portalStyle={portalStyle}
      footer={
        <div style={{ display: 'flex', gap: 'var(--sp-3)', marginLeft: 'auto' }}>
          <Button
            variant="outline"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={working}
          >
            Cancel
          </Button>
          <Button
            variant="gradient"
            size="md"
            onClick={handleConfirm}
            disabled={working || scanning || blocked}
            style={{ background: '#dc2626', opacity: working || scanning || blocked ? 0.5 : 1 }}
          >
            {working ? 'Deleting…' : 'Delete Item'}
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-5) var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
        }}
      >
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          Permanently delete <strong>{itemName || 'this item'}</strong>?
        </p>

        {scanning && (
          <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            Checking related data…
          </p>
        )}

        {scan && scan.cascades.length > 0 && (
          <RefList title="This will also remove:" tone="neutral" rows={scan.cascades} />
        )}

        {scan && scan.blockers.length > 0 && (
          <RefList
            title="Cannot delete — this item is used by:"
            tone="danger"
            rows={scan.blockers}
            footerNote="Resolve or remove these records first, then delete the item."
          />
        )}

        {scan && scan.canDelete && scan.cascades.length === 0 && (
          <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            No related data found. This item can be safely deleted.
          </p>
        )}

        {error && (
          <div
            role="alert"
            style={{
              background: '#fee2e2',
              color: '#991b1b',
              border: '1px solid #fecaca',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--sp-3) var(--sp-4)',
              fontSize: 'var(--font-size-sm)',
              whiteSpace: 'pre-wrap',
            }}
          >
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

function RefList({
  title,
  rows,
  tone,
  footerNote,
}: {
  title: string;
  rows: { key: string; label: string; count: number }[];
  tone: 'neutral' | 'danger';
  footerNote?: string;
}) {
  const danger = tone === 'danger';
  return (
    <div
      style={{
        background: danger ? '#fef2f2' : 'var(--bg-surface-2, #f9fafb)',
        border: `1px solid ${danger ? '#fecaca' : 'var(--border)'}`,
        borderRadius: 'var(--radius-md)',
        padding: 'var(--sp-3) var(--sp-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          color: danger ? '#991b1b' : 'var(--text-muted)',
        }}
      >
        {title}
      </span>
      <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map((r) => (
          <li key={r.key} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
            <strong>{r.count}</strong> {r.label}
          </li>
        ))}
      </ul>
      {footerNote && (
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: danger ? '#991b1b' : 'var(--text-muted)',
          }}
        >
          {footerNote}
        </span>
      )}
    </div>
  );
}
